import { Component, Match, Switch, createEffect, createMemo, createSignal, on } from 'solid-js'
import { AppSchema } from '/common/types/schema'
import Select from '/web/shared/Select'
import Modal from '/web/shared/Modal'
import Button from '/web/shared/Button'
import { Save, X } from 'lucide-solid'
import { exportCharacter } from '/common/characters'
import { charsApi } from '/web/store/data/chars'
import { toastStore, i18nStore } from '/web/store'
import { downloadCharCard } from './util'
import { downloadJson } from '/web/shared/util'

type CharacterFileType = 'png' | 'json'

/**
 * WIP: Enable downloading characters in different persona formats for different application targets
 */

export const DownloadModal: Component<{
  show: boolean
  close: () => void
  charId: string
  char?: AppSchema.Character
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  let ref: any
  const [char, setChar] = createSignal<AppSchema.Character | undefined>(props.char)

  createEffect(async () => {
    if (!props.char && !props.charId) {
      setChar(undefined)
      return
    }

    if (props.char) {
      setChar(props.char)
      return
    }

    const res = await charsApi.getCharacterDetail(props.charId)
    if (res.result) {
      setChar(res.result)
    }

    if (res.error) {
      toastStore.error(`${i18n.t.char_downloadRetreiveFailed} ${res.error}`)
    }
  })

  const fileTypeItems = createMemo(() => {
    const opts = [{ value: 'json', label: 'JSON' }]
    if (char()?.avatar) {
      opts.unshift({ value: 'png', label: i18n.t.char_imageCard })
    }
    return opts
  })

  const [format, setFormat] = createSignal('tavern')
  const [fileType, setFileType] = createSignal<string>(char()?.avatar ? 'png' : 'json')
  const outputs = createMemo(() => {
    // TODO: We don't need to support exporting in Agnaistic format
    // once we fully support Chara Card V2. We just need to put
    // Agnai-specific fields in the `extensions` prop.

    const base = [{ value: 'tavern', label: 'Tavern' }]
    if (fileType() === 'png') return base
    return base.concat([
      { value: 'native', label: 'HuanYouAI' },
      { value: 'ooba', label: i18n.t.char_textgen },
    ])
  })

  createEffect(
    on(
      () => char(),
      (char) => {
        if (!char) return
        if (char.avatar) {
          setFileType('png')
        }
      }
    )
  )

  const objectUrl = createMemo(() => {
    if (!char()) return

    const json = charToJson(char()!, format())
    return json
    // const url = URL.createObjectURL(new Blob([json], { type: 'text/json' }))
    // return url
  })

  return (
    <Modal
      show={props.show && !!char()}
      close={props.close}
      title={i18n.t.char_downloadCharacter}
      footer={
        <>
          <Button schema="secondary" onClick={props.close}>
            <X /> {i18n.t.close}
          </Button>
          <Switch>
            <Match when={fileType() === 'json'}>
              <Button
                onClick={() => {
                  const downloadableChar = objectUrl()
                  if (!downloadableChar) return
                  downloadJson(downloadableChar, `${char()?.name}.json`)
                }}
              >
                <Save /> {i18n.t.char_downloadJson}
              </Button>
            </Match>

            <Match when={fileType() === 'png'}>
              <Button disabled={!char()} onClick={() => downloadCharCard(char()!, format())}>
                <Save /> {i18n.t.char_downloadPng}
              </Button>
            </Match>
          </Switch>
        </>
      }
    >
      <form ref={ref} class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <Select
            label={i18n.t.char_outputFormat}
            fieldName="app"
            value={format()}
            items={outputs()}
            onChange={(item) => setFormat(item.value)}
          />
          <Select
            label={i18n.t.char_fileType}
            fieldName="fileType"
            value={fileType()}
            items={fileTypeItems()}
            onChange={(item) => setFileType(item.value as CharacterFileType)}
          />
        </div>
      </form>
    </Modal>
  )
}

function charToJson(char: AppSchema.Character, format: string) {
  const { _id, ...json } = char

  const copy = { ...char }

  if (format === 'native') {
    return JSON.stringify(json, null, 2)
  }

  const content = exportCharacter(copy, format as any)
  return JSON.stringify(content, null, 2)
}
